import { isLocaleCode, type LocaleCode } from "@/pages/cogo-marketing/i18n/locales";
import type { StudioContextualTaskItem, StudioContextualTaskMenu } from "./studio-contextual-task-menu";

/**
 * Localized chrome for the Studio Phase 3 contextual task menu (Recommended
 * disclosure + More disclosure + why-recommended / why-disabled reasons).
 * This module owns only that chrome — task labels themselves stay in
 * `@/i18n/studio-authority-copy.ts`.
 */
export interface StudioContextualTaskCopy {
  recommended: string;
  more: string;
  showLess: string;
  reason: Record<Exclude<StudioContextualTaskMenu["recommendationReason"], null>, string>;
  disabled: Record<Exclude<StudioContextualTaskItem["disabledReason"], null>, string>;
}

const EN: StudioContextualTaskCopy = {
  recommended: "Recommended",
  more: "More",
  showLess: "Show less",
  reason: {
    "resolve-blocker": "Resolve this before continuing",
    "edit-element": "Edit the selected element",
    "edit-block": "Edit the selected block",
    "edit-page": "Edit the selected page",
    "start-overview": "Start with the application overview",
    "review-release": "Review before you release",
  },
  disabled: {
    "target-unavailable": "The original target is no longer available.",
    "authority-unavailable": "This task isn't available right now.",
  },
};

const KO: StudioContextualTaskCopy = {
  recommended: "추천",
  more: "더 보기",
  showLess: "간략히 보기",
  reason: {
    "resolve-blocker": "계속하기 전에 이 문제를 해결하세요",
    "edit-element": "선택한 요소를 편집하세요",
    "edit-block": "선택한 블록을 편집하세요",
    "edit-page": "선택한 페이지를 편집하세요",
    "start-overview": "애플리케이션 개요부터 시작하세요",
    "review-release": "출시 전에 검토하세요",
  },
  disabled: {
    "target-unavailable": "원래 대상이 더 이상 존재하지 않습니다.",
    "authority-unavailable": "지금은 이 작업을 사용할 수 없습니다.",
  },
};

const JA: StudioContextualTaskCopy = {
  recommended: "おすすめ",
  more: "もっと見る",
  showLess: "表示を減らす",
  reason: {
    "resolve-blocker": "続行する前にこの問題を解決してください",
    "edit-element": "選択した要素を編集します",
    "edit-block": "選択したブロックを編集します",
    "edit-page": "選択したページを編集します",
    "start-overview": "アプリケーションの概要から始めましょう",
    "review-release": "公開前にレビューしてください",
  },
  disabled: {
    "target-unavailable": "元の対象はもう存在しません。",
    "authority-unavailable": "この操作は現在利用できません。",
  },
};

const ZH: StudioContextualTaskCopy = {
  recommended: "推荐",
  more: "更多",
  showLess: "收起",
  reason: {
    "resolve-blocker": "请先解决此问题再继续",
    "edit-element": "编辑所选元素",
    "edit-block": "编辑所选区块",
    "edit-page": "编辑所选页面",
    "start-overview": "从应用概览开始",
    "review-release": "发布前请先检查",
  },
  disabled: {
    "target-unavailable": "原始目标已不存在。",
    "authority-unavailable": "此操作暂不可用。",
  },
};

const ZH_TW: StudioContextualTaskCopy = {
  recommended: "推薦",
  more: "更多",
  showLess: "收合",
  reason: {
    "resolve-blocker": "請先解決此問題再繼續",
    "edit-element": "編輯所選元素",
    "edit-block": "編輯所選區塊",
    "edit-page": "編輯所選頁面",
    "start-overview": "從應用程式概覽開始",
    "review-release": "發佈前請先檢查",
  },
  disabled: {
    "target-unavailable": "原始目標已不存在。",
    "authority-unavailable": "此操作目前無法使用。",
  },
};

const STUDIO_CONTEXTUAL_TASK_COPY: Record<LocaleCode, StudioContextualTaskCopy> = {
  en: EN,
  ko: KO,
  ja: JA,
  zh: ZH,
  "zh-TW": ZH_TW,
};

/**
 * Locale is accepted as a loose `string` (not the closed `LocaleCode` union)
 * because callers may forward an arbitrary/unvalidated locale tag; an
 * unrecognized value falls back to English rather than throwing.
 */
export function getStudioContextualTaskCopy(locale: string): StudioContextualTaskCopy {
  const code: LocaleCode = isLocaleCode(locale) ? locale : "en";
  return STUDIO_CONTEXTUAL_TASK_COPY[code];
}
